import { Injectable, Injector } from '@angular/core';
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { catchError, Observable, ObservableInput, tap, throwError } from 'rxjs';
import { AuthService } from './services/auth.service';
import { RfJwtService } from './services/rf-jwt.service';
import { RoutingService } from './services/routing.service';
import { SegmentService } from './services/segment.service';
import { environment } from '../../environments/environment';
import { IS_BACKGROUND_REQUEST } from './constants/http-context-tokens';
import { SystemEmailsWarningService } from './services/system-emails-warning.service';

@Injectable()
export class RfHttpInterceptorInterceptor implements HttpInterceptor {
    private _authService: AuthService;
    private _routingService: RoutingService;
    private _segmentService: SegmentService;

    constructor(
        _authService: AuthService,
        _routingService: RoutingService,
        _segmentService: SegmentService,
        private injector: Injector,
    ) {
        this._authService = _authService;
        this._routingService = _routingService;
        this._segmentService = _segmentService;
    }

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler,
    ): Observable<HttpEvent<unknown>> {
        const headers = {
            Accept: 'application/json',
        };

        const token = RfJwtService.raw();
        if (token) {
            // @ts-expect-error array access
            headers['Authorization'] = 'Bearer ' + token;
        }

        const selectedSegment = this._segmentService.selectedSegment();
        if (selectedSegment) {
            // @ts-expect-error array access
            headers['X-Segment-Id'] = selectedSegment.id;
        }

        if (request.method !== 'GET') {
            // @ts-expect-error array access
            headers['Content-Type'] = 'application/json';
        }
        let alternateHeaders = null;
        if (
            request &&
            request.body &&
            request.body.constructor.name === 'FormData' &&
            token
        ) {
            alternateHeaders = {
                Authorization: 'Bearer ' + token,
                Accept: '*/*',
            };
        }

        if (request.context.get(IS_BACKGROUND_REQUEST)) {
            // @ts-expect-error array access
            headers['X-Background-Request'] = 'true';
        }

        if (alternateHeaders) {
            request = request.clone({
                url: request.url,
                setHeaders: alternateHeaders,
            });
        } else {
            request = request.clone({
                url: request.url,
                setHeaders: headers,
            });
        }

        if (environment.local) {
            let url = request.url;
            if (url.indexOf('?') === -1) {
                url = url + '?XDEBUG_SESSION_START=PHPSTORM';
            } else {
                url = url + '&XDEBUG_SESSION_START=PHPSTORM';
            }
            request = request.clone({
                url: url,
            });
        }

        return next.handle(request).pipe(
            tap((response) => {
                if (response instanceof HttpResponse) {
                    this.storeRefreshedToken(request, response);
                    this.reportBlockedEmails(response);
                }
            }),
            catchError<HttpEvent<any>, ObservableInput<any>>(
                (response: HttpErrorResponse) => {
                    if (response.status === 401) {
                        if (
                            response.error &&
                            response.error.msg &&
                            response.error.msg === 'Invalid recovery code'
                        ) {
                            return throwError(() => response);
                        }

                        // Remove JWT if it exists in local storage.
                        RfJwtService.clear();

                        return throwError(() => response);
                    }

                    // RF-2873: a failed response can carry a refreshed token too, and it
                    // is the only way out of a stale tab. JwtRefresh re-mints the JWT
                    // whenever its clientId claim has drifted from users.last_client, so
                    // when a tenant check refuses a request built from the old claim, the
                    // 403 arrives WITH a token naming the client the user actually
                    // switched to. Storing it only in tap() threw that correction away and
                    // left the tab 403ing on every client-scoped endpoint until some
                    // unguarded request happened to bring a fresh token back — recovery
                    // was real but nondeterministic. Handling it here makes the next
                    // request from that tab the one that recovers.
                    //
                    // Deliberately after the 401 branch: a 401 clears the JWT, and must
                    // not then re-store one.
                    this.storeRefreshedToken(request, response);

                    // 402 is the billing paywall, not an error to report. rf-api has
                    // decided this client is restricted until its outstanding invoice
                    // is paid, and the only useful reaction is to put the user on the
                    // page that lets them pay. Falling through instead raises a toast
                    // over whatever screen they were on, with its data missing and no
                    // route to the remedy — which is how a paywall reads as an outage.
                    //
                    // Deliberately after storeRefreshedToken: a 402 can carry a
                    // re-minted JWT like any other failure, and skipping it would
                    // strand a stale tab (RF-2873).
                    //
                    // Matched on the error code, not the status alone. 402 is generic
                    // enough that some later endpoint could mean something else by it,
                    // and a false positive here navigates the user off their work.
                    if (
                        response.status === 402 &&
                        (response.error?.error?.code ===
                            'subscription_past_due' ||
                            // A blob request — exports, document and image downloads,
                            // which is exactly what the second gated route group
                            // serves — delivers its error body as a Blob, which cannot
                            // be read synchronously here, so the code check above can
                            // never match. BillingRestriction is the only thing in
                            // rf-api that answers 402, so treat an unreadable 402 as
                            // the paywall rather than dropping the user on a generic
                            // toast with no route out. If a second 402 source ever
                            // appears, this is the line that needs narrowing.
                            response.error instanceof Blob)
                    ) {
                        // Already on the page: navigating again would re-enter the
                        // component, and its own calls can 402, so this would loop.
                        if (
                            !this._routingService
                                .getCurrentPath()
                                .startsWith('/client/billing-restricted')
                        ) {
                            this._routingService.navigate([
                                '/client/billing-restricted',
                            ]);
                        }

                        return throwError(() => response);
                    }

                    const responseErrors = response.error;
                    if (responseErrors) {
                        if (
                            responseErrors.errors &&
                            responseErrors.errors.length > 0
                        ) {
                            const errorText = responseErrors.errors
                                .map((err: any) => {
                                    if (err?.msg) {
                                        return err.msg;
                                    }

                                    return '';
                                })
                                .filter((err: any) => {
                                    return !!err;
                                })
                                .join(' ');

                            return throwError(
                                () =>
                                    new HttpErrorResponse({
                                        error: errorText,
                                        headers: response.headers,
                                        status: response.status,
                                        statusText: response.statusText,
                                    }),
                            );
                        }
                    }

                    return throwError(() => response);
                },
            ),
        );
    }

    /**
     * RF-2548: rf-api flags, on any staff request, emails the client's System
     * Emails setting will drop (ReportBlockedSystemEmails). Read here so no
     * caller has to be wired up for it. Resolved lazily: the warning service
     * reaches HttpClient (via AlertService), which depends on this interceptor.
     */
    private reportBlockedEmails(response: HttpResponse<unknown>): void {
        if (!Number(response.headers.get('X-Emails-Blocked'))) {
            return;
        }

        this.injector
            .get(SystemEmailsWarningService)
            .reportBlocked(
                response.headers.get('X-Show-Blocked-Banner') === '1',
            );
    }

    /**
     * Store the server's re-minted JWT, whether the response succeeded or failed.
     *
     * Background requests are excluded as before: they are fired without the user
     * doing anything, so letting one rewrite the stored token would change the
     * active client out from under whatever the user is actually looking at.
     */
    private storeRefreshedToken(
        request: HttpRequest<unknown>,
        response: HttpResponse<unknown> | HttpErrorResponse,
    ): void {
        if (request.context.get(IS_BACKGROUND_REQUEST)) {
            return;
        }

        if (!response.headers?.has('X-AccessToken')) {
            return;
        }

        RfJwtService.store(response.headers.get('X-AccessToken'));
    }
}
